import { useState } from 'react';
import type { CheckoutSession, FieldDef } from '@/lib/types';
import { CheckoutHeader } from '@/components/CheckoutHeader';

interface Props {
  session: CheckoutSession;
  selectedMethod: string;
  displayName: string;
  requiredFields: FieldDef[];
  onSubmit: (values: Record<string, string>) => void;
  onBack: () => void;
}

export function FieldCollection({ session, selectedMethod: _selectedMethod, displayName, requiredFields, onSubmit, onBack }: Props) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(requiredFields.map((f) => [f.key, ''])),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    for (const field of requiredFields) {
      const val = values[field.key]?.trim() ?? '';
      if (field.required && !val) {
        newErrors[field.key] = `${field.label} es requerido`;
      } else if (field.type === 'tel' && val) {
        // Colombian phone numbers: 10 digits, optionally starting with 57
        const digits = val.replace(/\D/g, '');
        const normalized = digits.startsWith('57') ? digits.slice(2) : digits;
        if (normalized.length !== 10) {
          newErrors[field.key] = 'Ingresa un número de celular válido (10 dígitos)';
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);

    // Normalize phone numbers before submitting
    const normalized: Record<string, string> = {};
    for (const field of requiredFields) {
      const val = values[field.key]?.trim() ?? '';
      if (field.type === 'tel') {
        const digits = val.replace(/\D/g, '');
        normalized[field.key] = digits.startsWith('57') ? digits.slice(2) : digits;
      } else {
        normalized[field.key] = val;
      }
    }
    onSubmit(normalized);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start p-4 pt-8 pb-8">
      <div className="w-full max-w-md">
        {/* Merchant + amount header */}
        <CheckoutHeader session={session} />

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Pagar con {displayName}</h2>
          <p className="text-sm text-gray-500 mb-5">Completa los siguientes datos para continuar.</p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-4">
              {requiredFields.map((field) => (
                <div key={field.key}>
                  <label htmlFor={field.key} className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  {field.type === 'select' ? (
                    <select
                      id={field.key}
                      value={values[field.key] ?? ''}
                      onChange={(e) => {
                        setValues((v) => ({ ...v, [field.key]: e.target.value }));
                        if (errors[field.key]) setErrors((err) => ({ ...err, [field.key]: '' }));
                      }}
                      disabled={isSubmitting}
                      className={`w-full px-4 py-3 rounded-xl border-2 text-gray-900 bg-white focus:outline-none focus:border-brand-500 transition-colors ${
                        errors[field.key] ? 'border-red-400' : 'border-gray-200'
                      } disabled:opacity-60`}
                    >
                      <option value="">Selecciona una opción</option>
                      {field.options?.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={field.key}
                      type={field.type}
                      value={values[field.key] ?? ''}
                      onChange={(e) => {
                        setValues((v) => ({ ...v, [field.key]: e.target.value }));
                        if (errors[field.key]) setErrors((err) => ({ ...err, [field.key]: '' }));
                      }}
                      placeholder={field.placeholder}
                      autoComplete={field.type === 'tel' ? 'tel' : 'off'}
                      disabled={isSubmitting}
                      className={`w-full px-4 py-3 rounded-xl border-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-500 transition-colors ${
                        errors[field.key] ? 'border-red-400' : 'border-gray-200'
                      } disabled:opacity-60`}
                    />
                  )}
                  {field.hint && !errors[field.key] && (
                    <p className="text-xs text-gray-400 mt-1">{field.hint}</p>
                  )}
                  {errors[field.key] && (
                    <p className="text-xs text-red-500 mt-1">{errors[field.key]}</p>
                  )}
                </div>
              ))}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-6 bg-brand-500 hover:bg-brand-600 text-white font-semibold py-3.5 px-6 rounded-xl transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[52px]"
            >
              {isSubmitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Procesando…
                </>
              ) : (
                `Pagar ahora`
              )}
            </button>
          </form>
        </div>

        {/* Back */}
        <button
          onClick={onBack}
          disabled={isSubmitting}
          className="w-full mt-4 text-sm text-gray-400 hover:text-gray-600 transition-colors py-3 min-h-[44px] disabled:opacity-50"
        >
          ← Elegir otro método de pago
        </button>

        <p className="text-center text-xs text-gray-400 mt-2 mb-2">
          Pago procesado de forma segura por Orangepill
        </p>
      </div>
    </div>
  );
}
